// Real render payloads for the design showcase, captured from a bprsky session
// on the local stack (agent_query_events, 11 Sep 2026). Each is the `data` of
// a table_rendered / chart_rendered event exactly as the sandbox emitted it —
// PlatinumData plus the spec that produced it — so the inline widget on the
// showcase is the real one, against the real job: its visibility toggles
// re-run the spec through /tables/bprsky/sky-uk-be-prius/run, its chart types
// are the real chart types. Only `toolCallId` is rewritten to match the
// showcase's fixed conversation.
//
// The chart is the age-group table in graph mode: a chart IS a table drawn as
// a graph, which is exactly what the chart plugin does with it.
//
// The composite table is the CompositeTable test fixture: a table the agent
// assembled with per-cell provenance. Its source datasets live in a session
// workspace, so the provenance modal's "load source" will report that there is
// no such file here — that message is real too.

export const TABLE_BY_GENDER = {
  "job": "sky-uk-be-prius",
  "spec": "{\"top\":\"gender(1/2)\",\"side\":\"BrandConsideration(33)\",\"useWeight\":true}",
  "title": "Sky Consideration Rate by Gender",
  "customer": "bprsky",
  "platinumData": {
    "top": {
      "vecs": [
        {
          "type": "Code",
          "label": "Male",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "Female",
          "allowPC": true
        }
      ],
      "groups": [
        {
          "h": 1,
          "w": 2,
          "x": 2,
          "y": 0,
          "text": "Gender"
        }
      ]
    },
    "uid": "3c1d73fa-ff28-4b21-b4be-a7ab9b8010c0",
    "meta": {
      "top": "Gender(Male/Female)",
      "side": "Which of these providers would you consider?(Sky)",
      "sigLevel": 0.999
    },
    "name": "GenTab16",
    "side": {
      "vecs": [
        {
          "type": "Code",
          "label": "Sky",
          "allowPC": true
        }
      ],
      "groups": [
        {
          "h": 1,
          "w": 1,
          "x": 0,
          "y": 2,
          "text": "Which of these providers would you consider?"
        }
      ]
    },
    "cells": {
      "rows": [
        {
          "cell": [
            {
              "freq": 76699,
              "colpc": 0.09365220268701074,
              "rowpc": 0.4558876849281685
            },
            {
              "freq": 90292,
              "colpc": 0.08205074147546301,
              "rowpc": 0.5366824971320903
            }
          ]
        }
      ]
    },
    "createdUtc": "2026-03-23T17:52:55.5922026Z",
    "properties": {
      "top": {
        "hide": {
          "empty": false,
          "active": false,
          "missing": false
        },
        "sort": {
          "key": 0,
          "band": "First",
          "type": "Value",
          "active": false,
          "ungrouped": false,
          "increasing": false
        },
        "groups": {
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "labels": {
          "width": 0,
          "height": 0,
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "baseCount": false
      },
      "side": {
        "hide": {
          "empty": false,
          "active": false,
          "missing": false
        },
        "sort": {
          "key": 0,
          "band": "First",
          "type": "Value",
          "active": false,
          "ungrouped": false,
          "increasing": false
        },
        "groups": {
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "labels": {
          "width": 0,
          "height": 0,
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "baseCount": false
      },
      "cells": {
        "blankChar": "-",
        "blankAsChar": false,
        "frequencies": false,
        "percentSign": false,
        "rowPercents": false,
        "zeroAsBlank": false,
        "missingAsZero": false,
        "columnPercents": true,
        "missingAsBlank": false,
        "showRedundant100": false,
        "thousandsSeparator": false,
        "percentsAsProportions": false
      },
      "corner": {
        "netResp": false,
        "priority": "Side"
      },
      "output": {
        "format": "None"
      },
      "titles": {
        "top": true,
        "name": true,
        "side": true,
        "filter": true,
        "status": true,
        "weight": true,
        "labelling": {
          "desc": false,
          "name": false,
          "codes": false,
          "script": false
        }
      },
      "decimals": {
        "percents": 2,
        "statistics": 1,
        "expressions": 2,
        "frequencies": 0
      },
      "significance": {
        "type": "SingleCell",
        "letters": "None",
        "visible": false,
        "hiLoTest": false,
        "meanStat": "Z",
        "propStat": "Z",
        "autoParam": false,
        "headcount": "Weighted",
        "oneTailed": false,
        "sigLevel1": 0.999,
        "sigLevel2": 0.99,
        "sigLevel3": 0.95,
        "skipCell5": false,
        "skipBase30": false,
        "lettersLong": false,
        "appendLetters": false,
        "propPooledEst": false,
        "continuityCorr": false,
        "meanPoolVariance": false
      }
    },
    "schemaVersion": "1.0.0"
  },
  "toolCallId": "tc-table"
} as const
export const TABLE_BY_AGE = {
  "job": "sky-uk-be-prius",
  "spec": "{\"top\":\"ageband1(*)\",\"side\":\"BrandConsideration(33)\",\"useWeight\":true}",
  "title": "Sky Consideration Rate by Age Group",
  "customer": "bprsky",
  "platinumData": {
    "top": {
      "vecs": [
        {
          "type": "Code",
          "label": "Under 18",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "18 to 24",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "25 to 34",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "35 to 44",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "45 to 54",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "55 to 64",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "65 to 74",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "75+",
          "allowPC": true
        }
      ],
      "groups": [
        {
          "h": 1,
          "w": 8,
          "x": 2,
          "y": 0,
          "text": "Age group"
        }
      ]
    },
    "uid": "9e278b6b-0a55-4fd9-8da9-e7bd8ab8a6ef",
    "meta": {
      "top": "Age group(All Codes)",
      "side": "Which of these providers would you consider?(Sky)",
      "sigLevel": 0.999
    },
    "name": "GenTab15",
    "side": {
      "vecs": [
        {
          "type": "Code",
          "label": "Sky",
          "allowPC": true
        }
      ],
      "groups": [
        {
          "h": 1,
          "w": 1,
          "x": 0,
          "y": 2,
          "text": "Which of these providers would you consider?"
        }
      ]
    },
    "cells": {
      "rows": [
        {
          "cell": [
            {
              "freq": 134,
              "colpc": 0.18356164383561643,
              "rowpc": 0.0007964764831402571
            },
            {
              "freq": 11887,
              "colpc": 0.08351131094562315,
              "rowpc": 0.07065459667976295
            },
            {
              "freq": 33435,
              "colpc": 0.09837758632864911,
              "rowpc": 0.19873277025219774
            },
            {
              "freq": 38138,
              "colpc": 0.09510652040009676,
              "rowpc": 0.22668671726868006
            },
            {
              "freq": 32405,
              "colpc": 0.09079396033140286,
              "rowpc": 0.192610600269851
            },
            {
              "freq": 31055,
              "colpc": 0.08088334878681488,
              "rowpc": 0.18458639689493048
            },
            {
              "freq": 21094,
              "colpc": 0.06803923529241002,
              "rowpc": 0.12537966369672077
            },
            {
              "freq": 93,
              "colpc": 0.15525876460767946,
              "rowpc": 0.0005527784547167457
            }
          ]
        }
      ]
    },
    "createdUtc": "2026-03-23T17:52:55.4035059Z",
    "properties": {
      "top": {
        "hide": {
          "empty": false,
          "active": false,
          "missing": false
        },
        "sort": {
          "key": 0,
          "band": "First",
          "type": "Value",
          "active": false,
          "ungrouped": false,
          "increasing": false
        },
        "groups": {
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "labels": {
          "width": 0,
          "height": 0,
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "baseCount": false
      },
      "side": {
        "hide": {
          "empty": false,
          "active": false,
          "missing": false
        },
        "sort": {
          "key": 0,
          "band": "First",
          "type": "Value",
          "active": false,
          "ungrouped": false,
          "increasing": false
        },
        "groups": {
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "labels": {
          "width": 0,
          "height": 0,
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "baseCount": false
      },
      "cells": {
        "blankChar": "-",
        "blankAsChar": false,
        "frequencies": false,
        "percentSign": false,
        "rowPercents": false,
        "zeroAsBlank": false,
        "missingAsZero": false,
        "columnPercents": true,
        "missingAsBlank": false,
        "showRedundant100": false,
        "thousandsSeparator": false,
        "percentsAsProportions": false
      },
      "corner": {
        "netResp": false,
        "priority": "Side"
      },
      "output": {
        "format": "None"
      },
      "titles": {
        "top": true,
        "name": true,
        "side": true,
        "filter": true,
        "status": true,
        "weight": true,
        "labelling": {
          "desc": false,
          "name": false,
          "codes": false,
          "script": false
        }
      },
      "decimals": {
        "percents": 2,
        "statistics": 1,
        "expressions": 2,
        "frequencies": 0
      },
      "significance": {
        "type": "SingleCell",
        "letters": "None",
        "visible": false,
        "hiLoTest": false,
        "meanStat": "Z",
        "propStat": "Z",
        "autoParam": false,
        "headcount": "Weighted",
        "oneTailed": false,
        "sigLevel1": 0.999,
        "sigLevel2": 0.99,
        "sigLevel3": 0.95,
        "skipCell5": false,
        "skipBase30": false,
        "lettersLong": false,
        "appendLetters": false,
        "propPooledEst": false,
        "continuityCorr": false,
        "meanPoolVariance": false
      }
    },
    "schemaVersion": "1.0.0"
  },
  "toolCallId": "tc-ok"
} as const
export const CHART_BY_AGE = {
  "job": "sky-uk-be-prius",
  "spec": "{\"top\":\"ageband1(*)\",\"side\":\"BrandConsideration(33)\",\"useWeight\":true}",
  "title": "Sky consideration by age group",
  "customer": "bprsky",
  "platinumData": {
    "top": {
      "vecs": [
        {
          "type": "Code",
          "label": "Under 18",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "18 to 24",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "25 to 34",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "35 to 44",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "45 to 54",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "55 to 64",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "65 to 74",
          "allowPC": true
        },
        {
          "type": "Code",
          "label": "75+",
          "allowPC": true
        }
      ],
      "groups": [
        {
          "h": 1,
          "w": 8,
          "x": 2,
          "y": 0,
          "text": "Age group"
        }
      ]
    },
    "uid": "9e278b6b-0a55-4fd9-8da9-e7bd8ab8a6ef",
    "meta": {
      "top": "Age group(All Codes)",
      "side": "Which of these providers would you consider?(Sky)",
      "sigLevel": 0.999
    },
    "name": "GenTab15",
    "side": {
      "vecs": [
        {
          "type": "Code",
          "label": "Sky",
          "allowPC": true
        }
      ],
      "groups": [
        {
          "h": 1,
          "w": 1,
          "x": 0,
          "y": 2,
          "text": "Which of these providers would you consider?"
        }
      ]
    },
    "cells": {
      "rows": [
        {
          "cell": [
            {
              "freq": 134,
              "colpc": 0.18356164383561643,
              "rowpc": 0.0007964764831402571
            },
            {
              "freq": 11887,
              "colpc": 0.08351131094562315,
              "rowpc": 0.07065459667976295
            },
            {
              "freq": 33435,
              "colpc": 0.09837758632864911,
              "rowpc": 0.19873277025219774
            },
            {
              "freq": 38138,
              "colpc": 0.09510652040009676,
              "rowpc": 0.22668671726868006
            },
            {
              "freq": 32405,
              "colpc": 0.09079396033140286,
              "rowpc": 0.192610600269851
            },
            {
              "freq": 31055,
              "colpc": 0.08088334878681488,
              "rowpc": 0.18458639689493048
            },
            {
              "freq": 21094,
              "colpc": 0.06803923529241002,
              "rowpc": 0.12537966369672077
            },
            {
              "freq": 93,
              "colpc": 0.15525876460767946,
              "rowpc": 0.0005527784547167457
            }
          ]
        }
      ]
    },
    "createdUtc": "2026-03-23T17:52:55.4035059Z",
    "properties": {
      "top": {
        "hide": {
          "empty": false,
          "active": false,
          "missing": false
        },
        "sort": {
          "key": 0,
          "band": "First",
          "type": "Value",
          "active": false,
          "ungrouped": false,
          "increasing": false
        },
        "groups": {
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "labels": {
          "width": 0,
          "height": 0,
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "baseCount": false
      },
      "side": {
        "hide": {
          "empty": false,
          "active": false,
          "missing": false
        },
        "sort": {
          "key": 0,
          "band": "First",
          "type": "Value",
          "active": false,
          "ungrouped": false,
          "increasing": false
        },
        "groups": {
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "labels": {
          "width": 0,
          "height": 0,
          "visible": true,
          "labelling": {
            "desc": false,
            "name": false
          }
        },
        "baseCount": false
      },
      "cells": {
        "blankChar": "-",
        "blankAsChar": false,
        "frequencies": false,
        "percentSign": false,
        "rowPercents": false,
        "zeroAsBlank": false,
        "missingAsZero": false,
        "columnPercents": true,
        "missingAsBlank": false,
        "showRedundant100": false,
        "thousandsSeparator": false,
        "percentsAsProportions": false
      },
      "corner": {
        "netResp": false,
        "priority": "Side"
      },
      "output": {
        "format": "None"
      },
      "titles": {
        "top": true,
        "name": true,
        "side": true,
        "filter": true,
        "status": true,
        "weight": true,
        "labelling": {
          "desc": false,
          "name": false,
          "codes": false,
          "script": false
        }
      },
      "decimals": {
        "percents": 2,
        "statistics": 1,
        "expressions": 2,
        "frequencies": 0
      },
      "significance": {
        "type": "SingleCell",
        "letters": "None",
        "visible": false,
        "hiLoTest": false,
        "meanStat": "Z",
        "propStat": "Z",
        "autoParam": false,
        "headcount": "Weighted",
        "oneTailed": false,
        "sigLevel1": 0.999,
        "sigLevel2": 0.99,
        "sigLevel3": 0.95,
        "skipCell5": false,
        "skipBase30": false,
        "lettersLong": false,
        "appendLetters": false,
        "propPooledEst": false,
        "continuityCorr": false,
        "meanPoolVariance": false
      }
    },
    "schemaVersion": "1.0.0"
  },
  "toolCallId": "tc-chart",
  "chartType": "bar"
} as const

export const COMPOSITE_TABLE = {
  toolCallId: 'tc-compose',
  id: 'composite-brand-index',
  title: 'Brand index — assembled',
  platinumData: {
    name: 'Brand index',
    top: { vecs: [{ label: 'Male' }, { label: 'Female' }] },
    side: { vecs: [{ label: 'Aware' }, { label: 'Considering' }] },
    cells: {
      rows: [
        { cell: [{ freq: 0.0659 }, { freq: 0.75 }] },
        { cell: [{ freq: 62.4 }, { freq: 58.1 }] },
      ],
    },
  },
  provenance: {
    cells: {
      '0,0': { kind: 'direct', description: '', unit: 'fraction', refs: [{ type: 'cell', dataset: 'awareness', row: 1, col: 0, rowLabel: 'Aware', colLabel: 'Male', metric: 'colpc', sourceValue: 0.0659 }] },
      '0,1': { kind: 'direct', description: '', unit: 'fraction', refs: [{ type: 'cell', dataset: 'awareness', row: 1, col: 1, rowLabel: 'Aware', colLabel: 'Female', metric: 'colpc', sourceValue: 0.75 }] },
      '1,0': { kind: 'derived', description: 'awareness minus rejection', unit: 'percent', refs: [] },
      '1,1': { kind: 'derived', description: 'awareness minus rejection', unit: 'percent', refs: [] },
    },
    // The run receipt names the script, so the Notebook shows it as a cell.
    run: { command: 'python3 data/brand_gap.py', script: 'data/brand_gap.py' },
  },
} as const

// The script the composite's run receipt names, for the Notebook's script cell.
export const BRAND_GAP_SCRIPT = `import json

awareness = json.load(open('data/awareness.json'))
rejection = json.load(open('data/rejection.json'))

# Consideration gap: awareness minus rejection, per gender column.
for col in range(2):
    a = awareness['cells']['rows'][1]['cell'][col]['colpc']
    r = rejection['cells']['rows'][1]['cell'][col]['colpc']
    print(col, round((a - r) * 100, 1))
`

// A real bprsky dashboard ("Prius Brand Equity - TV Brand Performance", four
// pages, table_graph + Gen AI insight + text widgets), captured from the local
// dashboards table. The inline widget draws it read-only through the same
// DashboardLayoutComponent the chat uses, so every table_graph fetches its
// table from the job for real.
export const DASHBOARD_CREATED = {
  toolCallId: 'tc-dash',
  dashboardId: '7185d7b2-0539-47c3-ae7a-8b17497c3ce7',
  customer: 'bprsky',
  viewUrl: '/dashboards/bprsky/7185d7b2-0539-47c3-ae7a-8b17497c3ce7/view',
  config: {
    "name": "Prius Brand Equity - TV Brand Performance",
    "pages": [
      {
        "id": "1f98d002-f03e-4672-afdf-1792371ceff1",
        "name": "Brand Funnel",
        "filter": "gender(1)",
        "layout": {
          "rows": [
            {
              "cells": [
                {
                  "widget_type": "title_hero",
                  "title_hero_data": {
                    "size": "compact",
                    "heading": "TV Brand Funnel",
                    "bg_color": "#1565c0",
                    "subtitle": "Prospects of each brand",
                    "alignment": "center",
                    "text_color": "#ffffff"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Consideration (prospects of each brand)",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC Con All Prospect"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "ITP 8-10  (of those who consider)",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC ITP All Prospect"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "multi_table_analysis",
                  "multi_table_analysis_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Summary",
                    "persona": "Executive Summarizer",
                    "multi_table_id": "12113665-2b37-44f1-9bbc-6bcb8efe2bab",
                    "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Start with an insight about Sky.  Not a generic overview sentence.\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns\n- Just give insights for Sky and one or 2 notable competitor brands.  That's it.  \n- no more than 50-100 words Keep it succinct. "
                  }
                },
                {
                  "widget_type": "multi_table_analysis",
                  "multi_table_analysis_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Consideration Insights",
                    "persona": "Executive Summarizer",
                    "multi_table_id": "b48f9a1a-4339-4777-a4e4-07178f380e81",
                    "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Start with an insight about Sky.  Not a generic overview sentence.\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns\n- Just give insights for Sky and one or 2 notable competitor brands.  That's it.  \n\nIMPORTANT: no more than 50-100 words in total. Keep it succinct. \nIMPORTANT: DO not add a title, we have already got that in the visual"
                  }
                },
                {
                  "widget_type": "multi_table_analysis",
                  "multi_table_analysis_data": {
                    "job": "sky-uk-be-prius",
                    "title": "ITP Summary",
                    "persona": "Executive Summarizer",
                    "multi_table_id": "9c287121-8a86-4ff7-b5c9-0c12880788be",
                    "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Start with an insight about Sky.  Not a generic overview sentence.\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns\n- Just give insights for Sky and one or 2 notable competitor brands.  That's it.  \n\nIMPORTANT: no more than 50-100 words in total. Keep it succinct. \nIMPORTANT: DO not add a title, we have already got that in the visual"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "display_props": {},
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Spontaneous Awareness (prospects of each brand)",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC Spon Aware All Prospect"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Consideration (prospects of each brand)",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC Con All Prospect"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Consideration (prospects of each brand)",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC Con All Prospect"
                  }
                }
              ]
            }
          ]
        }
      },
      {
        "id": "8800ce28-61ee-4274-910f-016c3d2db866",
        "name": "Brand Attributes",
        "filter": "gender(1)",
        "layout": {
          "rows": [
            {
              "cells": [
                {
                  "widget_type": "title_hero",
                  "title_hero_data": {
                    "size": "compact",
                    "heading": "TV Brand Attibutes",
                    "bg_color": "#1565c0",
                    "subtitle": "Sky Prospects",
                    "alignment": "center",
                    "text_color": "#ffffff"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "multi_table_analysis",
                  "multi_table_analysis_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Summary",
                    "persona": "Executive Summarizer",
                    "multi_table_id": "8a34ca2e-37ea-49a1-8836-6020f9de2e32",
                    "analysis_instructions": "Analyze the provided table data and generate insights in a structured format.\n\nYour response must follow this exact structure with these markdown headings:\n## Overview\n[One paragraph summary of what this table shows and why it matters]\n\n## Key Observations\n- [Specific metric with value and date] - what it demonstrates in longer-term context\n- [Longer-term trend pattern] with 3+ month or quarterly/yearly perspective\n- [Competitive insight] with specific gaps or advantages over time\n- Focus on patterns and trajectories, not single month changes\n- Contextualize latest month within broader trend narrative\n\n## Overall Trends\n[One paragraph describing longer-term trajectory and implications, avoiding single month-on-month analysis]\n\n## Conclusion\n[One sentence takeaway for executives focusing on sustained patterns]\n\nTREND ANALYSIS GUIDELINES:\n- Reference longer-view trends (3+ months, quarterly, yearly) rather than single month shifts\n- Anchor current performance to historical context\n- Use observational language: \"shows\", \"demonstrates\", \"indicates\", \"exhibits\"\n- Avoid \"meaningful\" or \"significant\" - use neutral descriptive terms\n- Keep analysis concise and data-driven with specific numbers and dates\n\nCRITICAL DATA FORMATTING:\n- Focus your analysis on COLUMN PERCENTAGES, not raw frequencies or row percentages. Column percentages show the distribution within each time period/category and are the key metrics for understanding market dynamics and trends"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Constantly innovates to deliver the best TV experience",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_23 DriversSP BestExp"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Offers content worth paying for",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_26 DriversSP Content"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "A range of shows to keep me entertained depending on my mood",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_27 DriversSP Rng"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Has great quality shows and programmes",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_24 DriversSP QualEnt"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Offers customers good value for what they receive",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_10 DriversSP Value"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Offers a simple and easy experience",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_5 DriversSP simp"
                  }
                }
              ]
            }
          ]
        }
      },
      {
        "id": "85ba0520-81f0-4c2a-8a99-4777b793c42f",
        "name": "Sky Customers",
        "filter": "gender(1)",
        "layout": {
          "rows": [
            {
              "cells": [
                {
                  "widget_type": "title_hero",
                  "title_hero_data": {
                    "size": "compact",
                    "heading": "Brand Attributes",
                    "bg_color": "#1565c0",
                    "subtitle": "Sky Customers",
                    "alignment": "center",
                    "text_color": "#ffffff"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "multi_table_analysis",
                  "multi_table_analysis_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Brand Attributes Summary",
                    "persona": "Executive Summarizer",
                    "multi_table_id": "0aaba08e-f48a-447b-9d6c-0a59381d8f23",
                    "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns",
                    "synthesis_instructions": "Write as a Sky employee, not an external consultant. Use professional, observational language appropriate for C-level executives.\n\nUse 1 decimal place ONLY for ITP metrics; use integers for all other percentages. Only use percentages, never fractions.\n\nFocus on longer-term trends rather than individual month shifts. Extract and synthesize findings from the individual analyses — do not create new interpretations beyond what the data shows."
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Constantly innovates to deliver the best TV experience",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_23 CustDrivers BestExp"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Offers content worth paying For",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_26 CustDrivers Content"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "A range of shows to keep me entertained\u000bdepending on my mood",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_27 CustDrivers Rng"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Has great quality shows and programmes",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_24 CustDrivers QualEnt"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Offers customers good value for what they receive",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_10 CustDrivers Value"
                  }
                },
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Offers a simple and easy experience",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC BI_5 CustDrivers Simple"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "title_hero",
                  "title_hero_data": {
                    "size": "compact",
                    "heading": "Intent To Stay - customers of each brand ",
                    "bg_color": "#1565c0",
                    "subtitle": "How likely is it that you will still be a customer of the following in 12 months' time? (% rating 6-10)",
                    "alignment": "center",
                    "text_color": "#ffffff"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "widget_type": "table_graph",
                  "table_graph_data": {
                    "job": "sky-uk-be-prius",
                    "title": "Customer Commitment - Sky TV Customers",
                    "data_type": "colPct",
                    "view_mode": "graph",
                    "chart_type": "line",
                    "table_path": "Tables/Exec/PPTX Reports/TV/TC Customers At Risk"
                  }
                }
              ]
            }
          ]
        }
      },
      {
        "id": "83149dcf-240f-4760-a3de-e8307c9149c2",
        "name": "BIPs",
        "filter": "gender(1)",
        "layout": {
          "rows": [
            {
              "cells": [
                {
                  "widget_type": "title_hero",
                  "title_hero_data": {
                    "size": "compact",
                    "heading": "BIPs charting",
                    "bg_color": "#1565c0",
                    "subtitle": "In development",
                    "alignment": "center",
                    "text_color": "#ffffff"
                  }
                }
              ]
            },
            {
              "cells": [
                {
                  "text_data": {
                    "content": ""
                  },
                  "widget_type": "text"
                },
                {
                  "text_data": {
                    "content": ""
                  },
                  "widget_type": "text"
                },
                {
                  "text_data": {
                    "content": ""
                  },
                  "widget_type": "text"
                }
              ]
            }
          ]
        }
      }
    ],
    "layout": {
      "rows": [
        {
          "cells": [
            {
              "widget_type": "title_hero",
              "title_hero_data": {
                "size": "compact",
                "heading": "TV Brand Funnel",
                "bg_color": "#1565c0",
                "subtitle": "Prospects of each brand",
                "alignment": "center",
                "text_color": "#ffffff"
              }
            }
          ]
        },
        {
          "cells": [
            {
              "widget_type": "table_graph",
              "table_graph_data": {
                "job": "sky-uk-be-prius",
                "title": "Consideration (prospects of each brand)",
                "data_type": "colPct",
                "view_mode": "graph",
                "chart_type": "line",
                "table_path": "Tables/Exec/PPTX Reports/TV/TC Con All Prospect"
              }
            },
            {
              "widget_type": "table_graph",
              "table_graph_data": {
                "job": "sky-uk-be-prius",
                "title": "ITP 8-10  (of those who consider)",
                "data_type": "colPct",
                "view_mode": "graph",
                "chart_type": "line",
                "table_path": "Tables/Exec/PPTX Reports/TV/TC ITP All Prospect"
              }
            }
          ]
        },
        {
          "cells": [
            {
              "widget_type": "multi_table_analysis",
              "multi_table_analysis_data": {
                "job": "sky-uk-be-prius",
                "title": "Summary",
                "persona": "Executive Summarizer",
                "multi_table_id": "12113665-2b37-44f1-9bbc-6bcb8efe2bab",
                "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Start with an insight about Sky.  Not a generic overview sentence.\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns\n- Just give insights for Sky and one or 2 notable competitor brands.  That's it.  \n- no more than 50-100 words Keep it succinct. "
              }
            },
            {
              "widget_type": "multi_table_analysis",
              "multi_table_analysis_data": {
                "job": "sky-uk-be-prius",
                "title": "Consideration Insights",
                "persona": "Executive Summarizer",
                "multi_table_id": "b48f9a1a-4339-4777-a4e4-07178f380e81",
                "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Start with an insight about Sky.  Not a generic overview sentence.\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns\n- Just give insights for Sky and one or 2 notable competitor brands.  That's it.  \n\nIMPORTANT: no more than 50-100 words in total. Keep it succinct. \nIMPORTANT: DO not add a title, we have already got that in the visual"
              }
            },
            {
              "widget_type": "multi_table_analysis",
              "multi_table_analysis_data": {
                "job": "sky-uk-be-prius",
                "title": "ITP Summary",
                "persona": "Executive Summarizer",
                "multi_table_id": "9c287121-8a86-4ff7-b5c9-0c12880788be",
                "analysis_instructions": "Write from a UK market perspective. Focus your analysis on the most recent month in the data vs the previous month. The most recent month is the LAST column in the table — do not assume or guess a month that is not in the data.\n\nTerminology:\n- Never say \"Sponsor awareness\" when you mean \"Spontaneous awareness\"\n- Never say \"Premium awareness\" — use \"Prompted awareness\"\n- Don't use \"non-Sky brands or Competitors\" — just use \"Competitors\"\n- Don't use the term \"shares\" or \"share\" — these are percentages of people selecting an answer in a survey\n- Don't use \"column percentage\" — just use \"percentage\"\n\nFormatting:\n- Use 1 decimal place ONLY for ITP metrics; use integers for all other percentages\n- Only use percentages, never fractions\n- Wrap segment names in single quotes (e.g., 'Prospects')\n- Wrap attribute names in double quotes (e.g., \"Value for Money\")\n- Always refer to attributes with the same name consistently\n- Avoid phrases like \"resilient brand positioning\" or \"evolving dynamics\"\n\nAnalysis Focus:\n- Start with an insight about Sky.  Not a generic overview sentence.\n- Stay focused on the latest month, not previous months\n- Don't refer to Sky when analysing competitor columns\n- Just give insights for Sky and one or 2 notable competitor brands.  That's it.  \n\nIMPORTANT: no more than 50-100 words in total. Keep it succinct. \nIMPORTANT: DO not add a title, we have already got that in the visual"
              }
            }
          ]
        },
        {
          "cells": [
            {
              "widget_type": "table_graph",
              "display_props": {},
              "table_graph_data": {
                "job": "sky-uk-be-prius",
                "title": "Spontaneous Awareness (prospects of each brand)",
                "data_type": "colPct",
                "view_mode": "graph",
                "chart_type": "line",
                "table_path": "Tables/Exec/PPTX Reports/TV/TC Spon Aware All Prospect"
              }
            },
            {
              "widget_type": "table_graph",
              "table_graph_data": {
                "job": "sky-uk-be-prius",
                "title": "Consideration (prospects of each brand)",
                "data_type": "colPct",
                "view_mode": "graph",
                "chart_type": "line",
                "table_path": "Tables/Exec/PPTX Reports/TV/TC Con All Prospect"
              }
            },
            {
              "widget_type": "table_graph",
              "table_graph_data": {
                "job": "sky-uk-be-prius",
                "title": "Consideration (prospects of each brand)",
                "data_type": "colPct",
                "view_mode": "graph",
                "chart_type": "line",
                "table_path": "Tables/Exec/PPTX Reports/TV/TC Con All Prospect"
              }
            }
          ]
        }
      ]
    },
    "description": "",
    "sign_off_revoked": {
      "revoked_at": 1788515162980,
      "revoked_by": "serviceaccount@bayesprice.com",
      "previous_user": "serviceaccount@bayesprice.com"
    }
  },
}

// The two validate_provenance calls on the composite above, as the sandbox
// emits them: one 'initial' event whose derived cells are still awaiting the
// agent's replay, then a separate 'complete' event carrying the verdict. They
// are two TOOL CALLS, so the host folds them into two buckets and both cards
// draw — which is exactly what the panel is designed around (#1347 item 15).
const provCell = (
  row: number,
  col: number,
  rowLabel: string,
  colLabel: string,
  kind: 'direct' | 'derived',
  status: 'pass' | 'fail' | 'skipped',
  expected: number | null,
  actual: number | null,
  detail?: string,
) => ({ row, col, rowLabel, colLabel, kind, status, expected, actual, ...(detail ? { detail } : {}) })

const PROV_ROWS: Array<[number, string, 'direct' | 'derived']> = [
  [0, 'Aware', 'direct'],
  [1, 'Considering', 'direct'],
  [2, 'Rejecting', 'direct'],
  [3, 'Index', 'derived'],
]
const PROV_COLS = ['Male', 'Female', 'Total']
const PROV_VALUE = [[85.2, 79.4, 82.3], [62.4, 58.1, 60.2], [11.6, 14.9, 13.2], [1.37, 1.37, 1.37]]

export const PROVENANCE_INITIAL = {
  toolCallId: 'tc-validate-1',
  dataset: 'brand_index',
  phase: 'initial',
  results: PROV_ROWS.flatMap(([row, rowLabel, kind]) =>
    PROV_COLS.map((colLabel, col) => (kind === 'direct'
      ? provCell(row, col, rowLabel, colLabel, kind, 'pass', PROV_VALUE[row][col], PROV_VALUE[row][col])
      : provCell(row, col, rowLabel, colLabel, kind, 'skipped', null, null, 'awaiting replay')))),
}

export const PROVENANCE_COMPLETE = {
  toolCallId: 'tc-validate-2',
  dataset: 'brand_index',
  phase: 'complete',
  // Ten pass, one fails on a mis-scaled percentage, one derived cell cannot be
  // checked because its source is gone — the three outcomes the panel exists
  // to tell apart.
  results: PROV_ROWS.flatMap(([row, rowLabel, kind]) =>
    PROV_COLS.map((colLabel, col) => {
      if (row === 1 && col === 1) {
        return provCell(row, col, rowLabel, colLabel, kind, 'fail', 58.1, 0.581, 'the displayed value disagrees with its source')
      }
      if (row === 3 && col === 2) {
        return provCell(row, col, rowLabel, colLabel, kind, 'skipped', null, null, 'source dataset "rejection_by_region" is missing')
      }
      return provCell(row, col, rowLabel, colLabel, kind, 'pass', PROV_VALUE[row][col], PROV_VALUE[row][col])
    })),
}

// flag_advanced_analysis: the notice card a turn gets when it runs a method
// nobody has checked. Reviewer set, as on a customer that filled in Stats review.
export const ANALYSIS_FLAGGED = {
  toolCallId: 'tc-flag-1',
  id: 'flag-showcase-1',
  method: 'key_driver',
  method_label: 'key driver analysis',
  summary: 'Shapley regression of brand consideration on eight perception statements',
  reviewer: { name: 'Priya Shah', contact: 'priya.shah@example.com', team: 'Insights' },
  line: "AI-generated analysis: this key driver analysis was produced by Platinum's AI and its method has not been checked. Before you share it, speak to Priya Shah (Insights, priya.shah@example.com).",
  created_at: 1760000000000,
}

// The warehouse_query card (agent warehouse_query tool): one successful query
// and one the server refused. The showcase has no signed-in owner, so the
// cards draw as a colleague sees them, Run disabled with its reason.
export const WAREHOUSE_QUERY_OK = {
  toolCallId: 'tc-wh-1',
  id: 'warehouse_query-tc-wh-1-0',
  connection_id: 'wh-demo-bq',
  connection_name: 'Sky BigQuery',
  connection_type: 'bigquery',
  title: 'Weekly active viewers by region',
  sql: "SELECT region, COUNT(DISTINCT viewer_id) AS viewers\nFROM `sky-demo.viewing.events`\nWHERE week = '2026-W40'\nGROUP BY region\nORDER BY viewers DESC",
  columns: [
    { name: 'region', type: 'STRING' },
    { name: 'viewers', type: 'INT64' },
  ],
  rows: [
    { region: 'London', viewers: 1204311 },
    { region: 'South East', viewers: 984120 },
    { region: 'North West', viewers: 871004 },
    { region: 'Midlands', viewers: 802376 },
    { region: 'Scotland', viewers: 410922 },
  ],
  row_count: 12,
  truncated: false,
  bytes_processed: 48234496,
  duration_ms: 1840,
  saved_path: 'data/viewers_by_region.json',
  query_log_id: 'ql-demo-1',
  error: '',
}

export const WAREHOUSE_QUERY_ERROR = {
  toolCallId: 'tc-wh-2',
  id: 'warehouse_query-tc-wh-2-0',
  connection_id: 'wh-demo-bq',
  connection_name: 'Sky BigQuery',
  connection_type: 'bigquery',
  title: '',
  sql: 'DELETE FROM `sky-demo.viewing.events` WHERE week = \'2026-W40\'',
  columns: [],
  rows: [],
  row_count: 0,
  truncated: false,
  bytes_processed: 0,
  duration_ms: 0,
  saved_path: '',
  query_log_id: '',
  error: 'Only read-only SELECT statements are allowed on this connection.',
}
